import {useEffect, useState, useRef, useReducer, useMemo} from 'react';
import {mutateAll, mutateAllByValue} from 'hooks/swr/swrHookFactory';
import {useLocation} from 'react-router-dom';
import Alert from 'helpers/Alert';
import {useInputMenuContext} from 'context/InputMenu';
import useParseURL from 'hooks/useParseURL';
import {useGetFishGroups} from 'hooks/swr';

import {inputMenuActionType, SWRKeyEnum} from 'interfaces';

import {editFishGroups, filterFishGroupsFromHook, resolveFishGroupIds} from './helpers/fishGroupHelpers';
import {actionTypeEnum, reducer} from './helpers/reducer';

const MAX_FISH_GROUPS = 30;
export function useFishGroups() {
  const {planId} = useParseURL();

  const location = useLocation();
  const inputContext = useInputMenuContext();
  const inputMenuDispatch = inputContext?.dispatch;
  const inputMenuState = inputContext?.state;
  const lastPlanId = useRef(planId);
  const originalPathname = useRef(location.pathname);

  const fishGroupIds = useMemo(
    () => inputMenuState?.formData && resolveFishGroupIds(inputMenuState?.formData),
    [inputMenuState?.formData],
  );
  const resourceId = fishGroupIds && fishGroupIds.length <= MAX_FISH_GROUPS ? [planId, fishGroupIds] : [planId, []];
  const {data} = useGetFishGroups({
    resourceId,
  });

  const fishGroups = useMemo(
    () => data && fishGroupIds && filterFishGroupsFromHook(data, fishGroupIds),
    [data, fishGroupIds],
  );
  const initialState = {
    fishGroupIds: fishGroupIds ?? [],
    cvEdited: false,
    nameEdited: false,
    qualityModelEdited: false,
    propertiesEdited: false,
    fishGroups: [],
    planId,
  };
  const [state, dispatch] = useReducer(reducer, initialState);

  const [updateFishGroups, setUpdateFishGroups] = useState<boolean>(false);

  useEffect(() => {
    // Drawer component was destroyed by React / Cleaning inputMenu state
    return () => {
      inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
    };
  }, [inputMenuDispatch]);

  useEffect(() => {
    if (location.pathname !== originalPathname.current || lastPlanId.current !== planId) {
      inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
    }
  }, [inputMenuDispatch, location, planId]);

  useEffect(() => {
    if (updateFishGroups && fishGroupIds && inputMenuDispatch) {
      editFishGroups(state)
        .then(() => {
          void mutateAll(SWRKeyEnum.GET_PLAN_FISH_GROUPS);
          state.fishGroupIds.forEach((id) => {
            void mutateAllByValue(id);
          });
          inputMenuState?.onClose && inputMenuState.onClose();
          inputMenuDispatch({type: inputMenuActionType.closeMenu});
        })
        .catch((error) => {
          Alert.error(error);
        })
        .finally(() => {
          setUpdateFishGroups(false);
        });
    }
  }, [fishGroupIds, inputMenuDispatch, inputMenuState, updateFishGroups]);

  useEffect(() => {
    if (fishGroups) {
      dispatch({type: actionTypeEnum.setInitialState, fishGroups});
    }
  }, [fishGroups]);

  return {
    state,
    dispatch,
    updateFishGroups,
    setUpdateFishGroups,
  };
}
